// Normalises a guess/answer for comparison: case, accents, punctuation,
// spacing and leading articles don't matter ("The Lion-King" == "lion king").
// Works for German (ß = ss, "Der Löwe" == "lowe"), French ("l'œuf" ==
// "oeuf") and Arabic too: short vowel marks, the different ways of writing
// alef, ya and ta marbuta, and a leading "ال" are ignored ("الأسد" == "اسد").
const ARTICLES = /^\s*(?:(?:the|a|an|der|die|das|den|dem|des|ein|eine|einen|einem|einer|le|la|les|un|une|du)\s+|l['’]\s*)/;
const DIGITS = { "٠": 0, "١": 1, "٢": 2, "٣": 3, "٤": 4, "٥": 5, "٦": 6, "٧": 7, "٨": 8, "٩": 9 };
export function normalizeAnswer(s) {
  const n = (s || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/[٠-٩]/g, (d) => DIGITS[d])
    .normalize("NFD")
    // Latin accents, then Arabic vowel marks, hamza marks and the tatweel.
    .replace(/[\u0300-\u036f\u064b-\u065f\u0670\u0640]/g, "")
    .replace(/[ٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/&/g, "and")
    .replace(ARTICLES, "")
    .replace(/[^\p{L}\p{N}]/gu, "");
  return /^ال[\u0621-\u064a]{2}/.test(n) ? n.slice(2) : n;
}

// Singular and plural both count: "lions" matches "Lion" and "headphone"
// matches "Headphones". German typed without umlauts works too: "Loewe"
// matches "Löwe".
export function answerVariants(s) {
  const n = normalizeAnswer(s);
  const out = new Set([n]);
  if (n.length > 3 && n.endsWith("s")) out.add(n.slice(0, -1));
  if (n.length > 2 && !n.endsWith("s") && /[a-z]$/.test(n)) out.add(`${n}s`);
  if (/[aou]e/.test(n)) out.add(n.replace(/([aou])e/g, "$1"));
  return [...out];
}

export async function sha256(s) {
  const data = new TextEncoder().encode(s);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

// Hashes are salted per room/turn so they can't be looked up across games.
export const hashAnswer = (answer, salt) => sha256(`${salt}:${normalizeAnswer(answer)}`);

export async function guessMatches(guess, hashes, salt) {
  if (!hashes || !hashes.length) return false;
  for (const v of answerVariants(guess)) {
    if (hashes.includes(await sha256(`${salt}:${v}`))) return true;
  }
  return false;
}

// Masks slurs and the strongest swear words (English, German, French and
// Arabic), including common l33t spellings ("sh1t", "f.u.c.k"). Short words
// that hide inside innocent ones ("spic" in "spicy", "pute" in "député",
// "كس" in "كسر") only count on their own. Spaces aren't skipped, so "this hit" is fine.
const BAD_ANYWHERE = [
  "fuck", "shit", "bitch", "cunt", "nigger", "nigga", "faggot", "retard", "whore", "wanker", "motherfucker",
  "fotze", "wichser", "hurensohn", "schwuchtel", "missgeburt", "kanake", "neger",
  "putain", "enculé", "encule", "connard", "connasse", "bougnoule", "youpin",
  "كسمك", "شرموط", "منيوك", "قحبة",
];
const BAD_ALONE = [
  "fag", "fags", "kike", "kikes", "spic", "spics", "chink", "chinks", "tranny", "slut", "sluts", "twat", "dyke", "coon", "paki",
  "spast", "spasti", "hure", "huren", "schlampe",
  "pute", "putes", "salope", "salopes", "pédé", "pédés", "nègre", "nègres",
  "كس", "زب", "خول", "عرص",
];
const LOOKALIKE = { a: "[a@4]", e: "[e3]", i: "[i1!|]", o: "[o0]", s: "[s$5]", t: "[t7+]", g: "[g9]", u: "[uv*]" };
const pattern = (w) => [...w].map((ch) => LOOKALIKE[ch] || ch).join("[._-]?");
// "On their own" means no letter or digit (in any alphabet) right before or after.
const BAD_RE = new RegExp(
  `${BAD_ANYWHERE.map(pattern).join("|")}|(^|[^\\p{L}\\p{N}])(${BAD_ALONE.map(pattern).join("|")})(?![\\p{L}\\p{N}])`,
  "giu"
);
// The swastika, both ways round (卐 卍, and the Tibetan signs that look the
// same), which is never shown: in chat it's refused and costs a strike (see
// lib/strikes.js), and anywhere else (names, answers, clues) it's masked.
const HATE_SYMBOLS = /[\u5350\u534D\u0FD5-\u0FD8]/gu;
export const hasHateSymbol = (s) => /[\u5350\u534D\u0FD5-\u0FD8]/u.test(s || "");

// Links aren't allowed anywhere players type: web addresses (http://…,
// www.…), invite links (discord.gg/…) and bare addresses like "example.com"
// (common endings only, so ordinary words and "Node.js" are left alone). The
// database also refuses chat messages with "://" or "www." (database.rules.json).
const LINK_RE = new RegExp(
  [
    "(?:https?|ftp):\\/\\/\\S+",
    "\\bwww\\.\\S+",
    "\\b(?:discord\\.gg|discord(?:app)?\\.com|t\\.me|bit\\.ly|youtu\\.be)\\/?\\S*",
    "\\b[\\p{L}\\p{N}-]+(?:\\.[\\p{L}\\p{N}-]+)*\\.(?:com|net|org|io|gg|tv|xyz|app|dev|link|ly|info|biz|ru|cc|ws|site|online|shop|store|club)(?![\\p{L}\\p{N}])(?:\\/\\S*)?",
  ].join("|"),
  "giu"
);
export const hasLink = (s) => new RegExp(LINK_RE.source, "iu").test(s || "");
export const maskLinks = (s) => (s || "").replace(LINK_RE, (m) => "*".repeat(Math.min(m.length, 12)));

export const maskBadWords = (s) =>
  maskLinks(s)
    .replace(HATE_SYMBOLS, "*")
    .replace(BAD_RE, (m, before, alone) => (alone === undefined ? "*".repeat(m.length) : before + "*".repeat(alone.length)));

export function cleanText(s, max = 140) {
  return maskBadWords((s || "").slice(0, max));
}
